import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import ForceGraph2D from "react-force-graph-2d";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import DogPicker from "@/components/DogPicker";
import PedigreeDogEditor from "@/components/PedigreeDogEditor";
import { DogIcon as DogIcon, GitForkIcon as GitFork, ArrowsClockwiseIcon as ArrowsClockwise, MagnifyingGlassIcon as MagnifyingGlass, CrosshairIcon as Crosshair, XIcon as X, WarningIcon as Warning, ArrowRightIcon as ArrowRight, PathIcon as PathIcon } from "@phosphor-icons/react";

/** Returns the connected-component (undirected) of `rootId` given a list of
 *  links. Used by Focus mode to restrict the graph to "everyone reachable
 *  from this dog" via sire/dam edges in either direction. */
function connectedComponent(rootId, links) {
  const adj = new Map();
  for (const l of links) {
    const s = typeof l.source === "object" ? l.source.id : l.source;
    const t = typeof l.target === "object" ? l.target.id : l.target;
    if (!adj.has(s)) adj.set(s, []);
    if (!adj.has(t)) adj.set(t, []);
    adj.get(s).push(t);
    adj.get(t).push(s);
  }
  const seen = new Set([rootId]);
  const queue = [rootId];
  while (queue.length) {
    const cur = queue.shift();
    for (const nb of adj.get(cur) || []) {
      if (!seen.has(nb)) {
        seen.add(nb);
        queue.push(nb);
      }
    }
  }
  return seen;
}

/** BFS shortest path between two dog ids via sire/dam edges (treated as
 *  undirected — line breeding can flow either way through common ancestors).
 *  Returns an ordered array of node ids `[fromId, …, toId]`, or `null` if
 *  no connection exists. Edges are also returned as `{source, target}` pairs
 *  matching the node ordering so the FE can highlight them precisely. */
function shortestPath(fromId, toId, links) {
  if (!fromId || !toId || fromId === toId) return null;
  const adj = new Map();
  for (const l of links) {
    const s = typeof l.source === "object" ? l.source.id : l.source;
    const t = typeof l.target === "object" ? l.target.id : l.target;
    if (!adj.has(s)) adj.set(s, []);
    if (!adj.has(t)) adj.set(t, []);
    adj.get(s).push({ to: t, type: l.type });
    adj.get(t).push({ to: s, type: l.type });
  }
  // BFS with parent-pointer reconstruction.
  const prev = new Map(); // id -> {from, type}
  const seen = new Set([fromId]);
  const queue = [fromId];
  while (queue.length) {
    const cur = queue.shift();
    if (cur === toId) {
      // Reconstruct
      const nodes = [toId];
      const edges = [];
      let c = toId;
      while (c !== fromId) {
        const p = prev.get(c);
        nodes.unshift(p.from);
        edges.unshift({ source: p.from, target: c, type: p.type });
        c = p.from;
      }
      return { nodes, edges };
    }
    for (const { to, type } of adj.get(cur) || []) {
      if (!seen.has(to)) {
        seen.add(to);
        prev.set(to, { from: cur, type });
        queue.push(to);
      }
    }
  }
  return null;
}

const SEX_COLOR = {
  male: "#1F6FEB",     // blue
  female: "#C1272D",   // red
  unknown: "#6B6B6B",  // grey
};

export default function AdminMasterPedigree() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({ nodes: [], links: [], orphans: [], breeds: [], stats: {} });
  const [breed, setBreed] = useState("__all__");
  const [mode, setMode] = useState("graph"); // "graph" | "focus" | "path"
  const [focusId, setFocusId] = useState("");
  const [editDog, setEditDog] = useState("");
  const nodesById = useMemo(() => {
    const m = {};
    for (const n of data.nodes) m[n.id] = n;
    return m;
  }, [data.nodes]);
  const [pathFromId, setPathFromId] = useState("");
  const [pathToId, setPathToId] = useState("");
  const [highlightId, setHighlightId] = useState(""); // hover highlight
  const [orphansOpen, setOrphansOpen] = useState(true);

  const fgRef = useRef(null);
  const wrapperRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 600 });

  // Resize the canvas when its parent <div> resizes.
  useEffect(() => {
    if (!wrapperRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const { width, height } = e.contentRect;
        setSize({ w: Math.max(320, Math.floor(width)), h: Math.max(360, Math.floor(height)) });
      }
    });
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, []);

  // Admin-only — bounce non-admins (they shouldn't have a link to this page
  // but the route guard in App.js doesn't check role).
  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  // Fetch graph data whenever the breed filter changes.
  const fetchGraph = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (breed && breed !== "__all__") params.breed = breed;
      const { data: payload } = await api.get("/admin/data/master-pedigree", { params });
      // Force-graph mutates the nodes/links objects in-place to add x/y/vx/vy
      // → make a deep-enough copy so subsequent re-renders aren't messed up.
      setData({
        nodes: payload.nodes.map((n) => ({ ...n })),
        links: payload.links.map((l) => ({ ...l })),
        orphans: payload.orphans || [],
        breeds: payload.breeds || [],
        stats: payload.stats || {},
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Failed to load registry graph";
      setError(typeof detail === "string" ? detail : "Failed to load registry graph");
    } finally {
      setLoading(false);
    }
  }, [breed]);

  useEffect(() => { fetchGraph(); }, [fetchGraph]);

  // Shortest sire/dam path between two dogs (Path mode). Treats edges as
  // undirected — line breeding flows through common ancestors either way.
  const pathResult = useMemo(() => {
    if (mode !== "path") return null;
    return shortestPath(pathFromId, pathToId, data.links);
  }, [mode, pathFromId, pathToId, data.links]);

  // Quick-lookup sets for rendering: which nodes/edges are part of the
  // currently-displayed shortest path.
  const pathNodeSet = useMemo(
    () => new Set(pathResult?.nodes || []),
    [pathResult]
  );
  const pathEdgeKey = useCallback(
    (l) => {
      const s = typeof l.source === "object" ? l.source.id : l.source;
      const t = typeof l.target === "object" ? l.target.id : l.target;
      // Undirected key
      return s < t ? `${s}|${t}` : `${t}|${s}`;
    },
    []
  );
  const pathEdgeSet = useMemo(() => {
    if (!pathResult) return new Set();
    return new Set(pathResult.edges.map((e) => (e.source < e.target ? `${e.source}|${e.target}` : `${e.target}|${e.source}`)));
  }, [pathResult]);

  // Compute the visible subset based on mode.
  const visible = useMemo(() => {
    if (mode === "focus" && focusId) {
      const comp = connectedComponent(focusId, data.links);
      const nodes = data.nodes.filter((n) => comp.has(n.id));
      const links = data.links.filter((l) => {
        const s = typeof l.source === "object" ? l.source.id : l.source;
        const t = typeof l.target === "object" ? l.target.id : l.target;
        return comp.has(s) && comp.has(t);
      });
      return { nodes, links };
    }
    if (mode === "path" && pathResult) {
      const ids = new Set(pathResult.nodes);
      const nodes = data.nodes.filter((n) => ids.has(n.id));
      const links = data.links.filter((l) => {
        const s = typeof l.source === "object" ? l.source.id : l.source;
        const t = typeof l.target === "object" ? l.target.id : l.target;
        return ids.has(s) && ids.has(t);
      });
      return { nodes, links };
    }
    return { nodes: data.nodes, links: data.links };
  }, [mode, focusId, pathResult, data]);

  // Re-zoom-to-fit after the graph (re)renders. Capped at zoom=3 so a
  // single isolated node (orphan in Focus mode) doesn't fill the canvas.
  useEffect(() => {
    if (!fgRef.current || visible.nodes.length === 0) return;
    const t = setTimeout(() => {
      try {
        fgRef.current.zoomToFit(400, 80);
        // After fit, clamp the zoom so single-node fits don't go absurd.
        setTimeout(() => {
          try {
            const z = fgRef.current.zoom();
            if (z > 3) fgRef.current.zoom(3, 200);
          } catch { /* canvas not ready */ }
        }, 450);
      } catch { /* canvas not ready */ }
    }, 350);
    return () => clearTimeout(t);
  }, [visible.nodes.length, visible.links.length, mode]);

  const onNodeClick = (node) => {
    if (mode === "focus") {
      // In focus mode a click recenters on the clicked dog.
      setFocusId(node.id);
    } else if (mode === "path") {
      // Path mode: click sets the From; shift-click sets the To.
      // Falling back to: if From is empty, set From; else set To.
      if (!pathFromId) setPathFromId(node.id);
      else if (!pathToId && node.id !== pathFromId) setPathToId(node.id);
      else setPathFromId(node.id); // restart
    } else {
      // Graph mode: open the inline editor — owner info + sire/dam controls.
      setEditDog(node.id);
    }
  };

  const drawNode = useCallback((node, ctx, globalScale) => {
    const isHighlighted = highlightId && highlightId === node.id;
    const isFocused = focusId && focusId === node.id;
    const isPathEnd = mode === "path" && (node.id === pathFromId || node.id === pathToId);
    const isPathInner = mode === "path" && pathNodeSet.has(node.id) && !isPathEnd;
    const baseR = isFocused || isPathEnd ? 8 : isPathInner ? 6 : 4 + Math.min(6, (node.parent_degree || 0) + (node.child_degree || 0));
    const r = isHighlighted ? baseR + 2 : baseR;
    const color = SEX_COLOR[node.sex] || SEX_COLOR.unknown;
    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI, false);
    ctx.fillStyle = color;
    ctx.fill();
    if (isFocused || isPathEnd) {
      ctx.lineWidth = 2.5 / globalScale;
      ctx.strokeStyle = "#C9A227"; // gold ring
      ctx.stroke();
    } else if (isPathInner) {
      ctx.lineWidth = 2 / globalScale;
      ctx.strokeStyle = "#C9A227";
      ctx.stroke();
    } else if (isHighlighted) {
      ctx.lineWidth = 1.5 / globalScale;
      ctx.strokeStyle = "#0E0E0E";
      ctx.stroke();
    }
    // Label when zoomed in OR for any highlighted / path / focused node.
    if (globalScale > 1.4 || isHighlighted || isFocused || isPathEnd || isPathInner) {
      const fontSize = Math.max(10, 12 / globalScale);
      ctx.font = `${fontSize}px Manrope, sans-serif`;
      ctx.fillStyle = "#0E0E0E";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(node.name || "?", node.x, node.y + r + 2);
    }
  }, [highlightId, focusId, mode, pathFromId, pathToId, pathNodeSet]);

  const focusOnDog = (dogId) => {
    setFocusId(dogId);
    setMode("focus");
  };

  // Build a human-readable lineage breadcrumb for Path mode.
  // e.g. "Thor →(sire) Apollo →(dam) Luna"
  const pathBreadcrumb = useMemo(() => {
    if (!pathResult || !data.nodes.length) return null;
    const byId = new Map(data.nodes.map((n) => [n.id, n]));
    const hops = pathResult.nodes.map((id) => byId.get(id)?.name || "?");
    const types = pathResult.edges.map((e) => e.type);
    return { hops, types, length: pathResult.edges.length };
  }, [pathResult, data.nodes]);

  return (
    <AppShell>
      <div className="space-y-6" data-testid="admin-master-pedigree">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-bold">Admin · Registry-wide</p>
            <h1 className="font-spectral text-3xl md:text-4xl mt-1">Master pedigree</h1>
            <p className="text-sm text-[#6B6B6B] mt-1 max-w-2xl">
              Every dog in the registry as a node, every recorded sire/dam relationship as an edge.
              Use this view to spot missing ancestry, isolated clusters, and how kennels interconnect.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchGraph}
            data-testid="reload-btn"
            className="rounded-full self-start md:self-auto"
          >
            <ArrowsClockwise size={14} className="mr-2" />
            Reload
          </Button>
        </div>

        {/* Toolbar */}
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-4 md:p-5 flex flex-wrap items-center gap-3 md:gap-4">
          {/* Mode toggle */}
          <div className="inline-flex rounded-full border border-[#E5E5E5] overflow-hidden text-sm" data-testid="mode-toggle">
            <button
              type="button"
              onClick={() => setMode("graph")}
              data-testid="mode-graph"
              className={`px-4 py-1.5 transition-colors ${mode === "graph" ? "bg-[#0E0E0E] text-white" : "text-[#3F3F3F] hover:bg-[#FAFAFA]"}`}
            >
              <GitFork size={14} weight="duotone" className="inline mr-1.5" />Graph
            </button>
            <button
              type="button"
              onClick={() => setMode("focus")}
              data-testid="mode-focus"
              className={`px-4 py-1.5 transition-colors ${mode === "focus" ? "bg-[#0E0E0E] text-white" : "text-[#3F3F3F] hover:bg-[#FAFAFA]"}`}
            >
              <Crosshair size={14} weight="duotone" className="inline mr-1.5" />Focus
            </button>
            <button
              type="button"
              onClick={() => setMode("path")}
              data-testid="mode-path"
              className={`px-4 py-1.5 transition-colors ${mode === "path" ? "bg-[#0E0E0E] text-white" : "text-[#3F3F3F] hover:bg-[#FAFAFA]"}`}
            >
              <PathIcon size={14} weight="duotone" className="inline mr-1.5" />Path
            </button>
          </div>

          {/* Breed filter (always visible) */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold">Breed</span>
            <Select value={breed} onValueChange={setBreed}>
              <SelectTrigger className="h-9 w-[200px] rounded-full border-[#E5E5E5]" data-testid="breed-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All breeds ({data.breeds.length})</SelectItem>
                {data.breeds.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Focus picker (focus mode only) */}
          {mode === "focus" && (
            <div className="flex items-center gap-2 text-sm flex-1 min-w-[260px] max-w-[420px]" data-testid="focus-picker-wrap">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold whitespace-nowrap">Focus on</span>
              <div className="flex-1">
                <DogPicker
                  value={focusId}
                  onChange={(id) => setFocusId(id)}
                  placeholder="Search any dog by name or reg #"
                  testId="focus-picker"
                />
              </div>
              {focusId && (
                <button
                  type="button"
                  onClick={() => setFocusId("")}
                  data-testid="clear-focus"
                  className="text-xs text-[#6B6B6B] hover:text-crimson transition-colors px-2"
                  title="Clear focus"
                >
                  <X size={14} weight="bold" />
                </button>
              )}
            </div>
          )}

          {/* Path pickers (path mode only) */}
          {mode === "path" && (
            <div className="flex items-center gap-2 text-sm flex-1 min-w-[320px]" data-testid="path-pickers-wrap">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold whitespace-nowrap">From</span>
              <div className="flex-1 min-w-[160px]">
                <DogPicker
                  value={pathFromId}
                  onChange={(id) => setPathFromId(id)}
                  placeholder="First dog"
                  testId="path-from-picker"
                />
              </div>
              <ArrowRight size={14} weight="bold" className="text-[#6B6B6B] flex-shrink-0" />
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6B6B] font-bold whitespace-nowrap">To</span>
              <div className="flex-1 min-w-[160px]">
                <DogPicker
                  value={pathToId}
                  onChange={(id) => setPathToId(id)}
                  placeholder="Second dog"
                  excludeIds={pathFromId ? [pathFromId] : []}
                  testId="path-to-picker"
                />
              </div>
              {(pathFromId || pathToId) && (
                <button
                  type="button"
                  onClick={() => { setPathFromId(""); setPathToId(""); }}
                  data-testid="clear-path"
                  className="text-xs text-[#6B6B6B] hover:text-crimson transition-colors px-2"
                  title="Clear both"
                >
                  <X size={14} weight="bold" />
                </button>
              )}
            </div>
          )}

          {/* Stats */}
          <div className="ml-auto flex items-center gap-4 text-xs text-[#6B6B6B]" data-testid="graph-stats">
            <span><strong className="text-ink">{visible.nodes.length}</strong> dogs</span>
            <span><strong className="text-ink">{visible.links.length}</strong> links</span>
            <span><strong className="text-ink">{data.stats.orphan_count || 0}</strong> orphans</span>
          </div>
        </div>

        {/* Errors */}
        {error && (
          <div className="bg-[#FEF2F2] border border-[#FCA5A5] text-[#991B1B] rounded-lg px-4 py-3 text-sm flex items-center gap-2" data-testid="graph-error">
            <Warning size={16} weight="duotone" />
            {error}
          </div>
        )}

        {/* Path result breadcrumb */}
        {mode === "path" && pathFromId && pathToId && (
          <div
            className={`rounded-xl px-4 py-3 text-sm border ${pathResult ? "bg-[#FBF7EC] border-[#E8D9A6]" : "bg-[#FEF2F2] border-[#FCA5A5]"}`}
            data-testid="path-result"
          >
            {pathResult ? (
              <div className="flex items-start gap-3 flex-wrap">
                <span className="inline-flex items-center bg-[#C9A227] text-white text-[10px] font-bold uppercase tracking-[0.18em] rounded-full px-2.5 py-1 flex-shrink-0">
                  {pathBreadcrumb.length}-hop link
                </span>
                <div className="flex-1 min-w-0 flex items-center gap-1.5 flex-wrap text-[#3F3F3F]" data-testid="path-breadcrumb">
                  {pathBreadcrumb.hops.map((name, i) => (
                    <React.Fragment key={i}>
                      <span className="font-semibold text-ink">{name}</span>
                      {i < pathBreadcrumb.types.length && (
                        <span className="text-[10px] uppercase tracking-[0.14em] text-gold font-bold">
                          → {pathBreadcrumb.types[i]} →
                        </span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-[#991B1B]" data-testid="path-none">
                <Warning size={14} weight="duotone" />
                <span>No recorded sire/dam path between these two dogs in the registry.</span>
              </div>
            )}
          </div>
        )}

        {/* Main layout: graph + orphans panel */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
          {/* Graph canvas */}
          <div
            ref={wrapperRef}
            className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden relative"
            style={{ height: "70vh", minHeight: 480 }}
            data-testid="graph-canvas-wrap"
          >
            {editDog && nodesById[editDog] && (
              <PedigreeDogEditor
                node={nodesById[editDog]}
                nodesById={nodesById}
                onClose={() => setEditDog("")}
                onChanged={fetchGraph}
              />
            )}
            {loading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-sm" data-testid="graph-loading">
                <div className="flex items-center gap-2 text-sm text-[#6B6B6B]">
                  <ArrowsClockwise size={16} className="animate-spin" />
                  Loading registry…
                </div>
              </div>
            )}

            {/* Empty states */}
            {!loading && visible.nodes.length === 0 && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 text-[#6B6B6B]">
                <DogIcon size={36} weight="duotone" className="text-gold" />
                <p className="text-sm mt-3 max-w-sm">
                  {mode === "focus" && !focusId
                    ? "Pick a dog above to focus the graph on its connected lineage."
                    : mode === "path" && (!pathFromId || !pathToId)
                    ? "Pick two dogs above to find the shortest sire/dam path between them."
                    : mode === "path"
                    ? "No recorded path between these two dogs."
                    : breed !== "__all__"
                    ? `No dogs in the registry with breed "${breed}".`
                    : "No dogs in the registry yet."}
                </p>
              </div>
            )}

            {!loading && visible.nodes.length > 0 && (
              <ForceGraph2D
                ref={fgRef}
                graphData={visible}
                width={size.w}
                height={size.h}
                backgroundColor="#FAFAFA"
                nodeRelSize={4}
                nodeCanvasObject={drawNode}
                nodeCanvasObjectMode={() => "replace"}
                linkColor={(l) => {
                  if (mode === "path" && pathEdgeSet.has(pathEdgeKey(l))) return "#C9A227"; // gold
                  return l.type === "sire" ? "#1F6FEB66" : "#C1272D66";
                }}
                linkDirectionalArrowLength={4}
                linkDirectionalArrowRelPos={1}
                linkWidth={(l) => {
                  if (mode === "path" && pathEdgeSet.has(pathEdgeKey(l))) return 3;
                  const s = typeof l.source === "object" ? l.source.id : l.source;
                  const t = typeof l.target === "object" ? l.target.id : l.target;
                  return s === focusId || t === focusId ? 2 : 1;
                }}
                cooldownTicks={120}
                onNodeClick={onNodeClick}
                onNodeHover={(n) => setHighlightId(n ? n.id : "")}
                onNodeRightClick={(n) => focusOnDog(n.id)}
              />
            )}

            {/* Legend */}
            <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm border border-[#E5E5E5] rounded-lg px-3 py-2 text-[11px] text-[#3F3F3F] flex items-center gap-3" data-testid="graph-legend">
              <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: SEX_COLOR.male }} />Male</span>
              <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: SEX_COLOR.female }} />Female</span>
              <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: SEX_COLOR.unknown }} />Unknown</span>
              <span className="ml-2 text-[#6B6B6B]">·</span>
              <span className="text-[#6B6B6B]">
                {mode === "focus"
                  ? "Click to recenter · Right-click to focus"
                  : mode === "path"
                  ? "Click to set From / To · Right-click to focus"
                  : "Click to open · Right-click to focus"}
              </span>
            </div>
          </div>

          {/* Orphans panel */}
          <aside className="bg-white border border-[#E5E5E5] rounded-xl flex flex-col" data-testid="orphans-panel" style={{ maxHeight: "70vh" }}>
            <button
              type="button"
              className="flex items-center justify-between gap-2 p-4 border-b border-[#E5E5E5] hover:bg-[#FAFAFA] transition-colors text-left"
              onClick={() => setOrphansOpen(!orphansOpen)}
              data-testid="orphans-toggle"
            >
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-gold font-bold">Orphans</p>
                <p className="font-spectral text-lg leading-tight">{data.orphans.length} unconnected</p>
                <p className="text-[11px] text-[#6B6B6B]">No recorded parents or offspring in {breed === "__all__" ? "the registry" : `"${breed}"`}.</p>
              </div>
              <span className="text-xs text-[#6B6B6B]">{orphansOpen ? "Hide" : "Show"}</span>
            </button>
            {orphansOpen && (
              <div className="flex-1 overflow-y-auto p-2" data-testid="orphans-list">
                {data.orphans.length === 0 && (
                  <p className="text-xs text-[#6B6B6B] text-center py-6">Every dog has at least one recorded parent or offspring. Nice work.</p>
                )}
                {data.orphans.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => focusOnDog(d.id)}
                    data-testid={`orphan-${d.id}`}
                    className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-md hover:bg-[#FAFAFA] transition-colors group"
                  >
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: SEX_COLOR[d.sex] || SEX_COLOR.unknown }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-ink truncate">
                        {d.name}
                        {d.registration_number && (
                          <span className="ml-1.5 text-[10px] font-mono text-[#6B6B6B]">{d.registration_number}</span>
                        )}
                      </p>
                      <p className="text-[11px] text-[#6B6B6B] truncate">{d.breed || "—"}</p>
                    </div>
                    <MagnifyingGlass size={12} weight="bold" className="text-[#6B6B6B] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            )}
          </aside>
        </div>
      </div>
    </AppShell>
  );
}
